import { addMonths, format, startOfMonth } from "date-fns";

import { numberPhaseSteps } from "../phaseSteps.js";

export const RECURRENT_PROJECTS_TUTORIAL_PREFERENCE_KEY = "recurrent_projects";
export const RECURRENT_PROJECTS_TUTORIAL_PARAM = "tutorial";
export const RECURRENT_PROJECTS_TUTORIAL_VALUE = "recurrent-projects";
export const RECURRENT_PROJECTS_TUTORIAL_PHASE_PARAM = "tutorialPhase";

export const RECURRENT_PROJECTS_TUTORIAL_PHASES = {
  OVERVIEW: "recurrent-projects-overview",
  MENU_TRIGGER: "recurrent-projects-menu-trigger",
  MENU: "recurrent-projects-menu",
  CONFIG_MODAL: "recurrent-projects-config-modal",
  CLIENT_CARD: "recurrent-projects-client-card",
};

export const RECURRENT_PROJECTS_TUTORIAL_INTRO = {
  title: "Antes de empezar",
  description:
    "Este tutorial te enseña cómo funcionan los proyectos recurrentes usando un ejemplo simulado.",
  badge: "Ejemplo guiado",
  highlightTitle: "Vas a ver cómo se generan y dónde se gestiona su recurrencia",
  highlightDescription:
    "Durante el tutorial verás una tabla, menús y modales con datos ficticios. No se modificará ninguna recurrencia real.",
  bulletTitle: "Durante el tutorial",
  bullets: [
    "Verás varios proyectos recurrentes de ejemplo y de dónde sale su estructura.",
    "Abrirás la configuración de recurrencia de un cliente para ver su estado y su próxima fecha.",
    "Verás que esa misma configuración también está disponible desde la página de Clientes.",
  ],
  note:
    "Al terminar entenderás de dónde salen estos proyectos, cuándo se generan y dónde revisar o pausar su recurrencia.",
  confirmLabel: "Ver tutorial",
};

export const RECURRENT_PROJECTS_TUTORIAL_COMPLETION_DESCRIPTION =
  "Has completado el tutorial de proyectos recurrentes. Ya sabes de dónde salen, cuándo se generan y dónde revisar o ajustar su recurrencia, tanto desde Proyectos como desde Clientes.";

// Fechas relativas al mes actual para que el ejemplo no quede desfasado.
const toIsoDate = (date) => format(date, "yyyy-MM-dd");

const firstDayOfMonth = (monthOffset) => startOfMonth(addMonths(new Date(), monthOffset));

const monthLabel = (date) => {
  const label = date.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
  return label.charAt(0).toUpperCase() + label.slice(1).replace(" de ", " ");
};

const CURRENT_MONTH = firstDayOfMonth(0);
const PREVIOUS_MONTH = firstDayOfMonth(-1);
const TWO_MONTHS_AGO = firstDayOfMonth(-2);
const NEXT_MONTH = firstDayOfMonth(1);

export const RECURRENT_PROJECTS_TUTORIAL_FEATURED_PROJECT_ID =
  "tutorial-recurrent-project-featured";

export const RECURRENT_PROJECTS_TUTORIAL_PROJECTS = [
  {
    id: RECURRENT_PROJECTS_TUTORIAL_FEATURED_PROJECT_ID,
    client: "tutorial-client-aurora",
    name: `SEO local ${monthLabel(CURRENT_MONTH)}`,
    client_name: "Clínica Aurora",
    template_name: "Pack Starter Mensual",
    state: "in_progress",
    progress_percentage: 42,
    start_date: toIsoDate(CURRENT_MONTH),
    is_internal: false,
    project_type: "recurrent",
  },
  {
    id: "tutorial-recurrent-project-02",
    client: "tutorial-client-zenit",
    name: `Growth ${monthLabel(CURRENT_MONTH)}`,
    client_name: "Academia Zenit",
    template_name: "Pack Premium Mensual",
    state: "not_started",
    progress_percentage: 0,
    start_date: toIsoDate(CURRENT_MONTH),
    is_internal: false,
    project_type: "recurrent",
  },
  {
    id: "tutorial-recurrent-project-03",
    client: "tutorial-client-litoral",
    name: `Contenido ${monthLabel(PREVIOUS_MONTH)}`,
    client_name: "Restaurante Litoral",
    template_name: "Pack Basic Mensual",
    state: "completed",
    progress_percentage: 100,
    start_date: toIsoDate(PREVIOUS_MONTH),
    is_internal: false,
    project_type: "recurrent",
  },
];

export const RECURRENT_PROJECTS_TUTORIAL_SUBSCRIPTION = {
  status: "active",
  next_run_date: toIsoDate(NEXT_MONTH),
};

export const RECURRENT_PROJECTS_TUTORIAL_CLIENT = {
  id: "tutorial-client-aurora",
  name: "Clínica Aurora",
  email: "hola@clinicaaurora.es",
  contact_name: "Laura Martín",
  agency_name: "Byneural",
  agency_id: 1,
  consultants: [],
  is_archived: false,
  logo: null,
  main_thread: null,
  created_at: toIsoDate(TWO_MONTHS_AGO),
  website: "https://clinicaaurora.es",
  drive_url: "https://drive.google.com",
  notebooklm_url: "",
  clientify_url: "",
  todolist_url: "",
  setup_project_info: {
    id: "tutorial-setup-project",
    state: "completed",
    progress_percentage: 100,
    setup_variant: "local",
  },
};

export const RECURRENT_PROJECTS_TUTORIAL_PACK_LABELS = {
  ecommerce: "Pack E-commerce",
  basic: "Pack Basic",
  premium: "Pack Premium",
  local: "Pack Starter",
  infoproducto: "Pack Infoproducto",
  sin_pack: "Sin Pack",
};

const baseStep = { placement: "auto", disableBeacon: true };

export const RECURRENT_PROJECTS_OVERVIEW_STEPS = [
  {
    ...baseStep,
    target: '[data-tutorial="projects-toolbar"]',
    title: "Buscar, filtrar y cambiar de vista",
    content:
      "Desde esta barra buscas proyectos por nombre o cliente, los filtras por estado (en progreso, no iniciados o completados) y abres los filtros avanzados. A la derecha eliges la vista: Kanban, tarjetas o tabla. Durante el tutorial usamos la vista de tabla.",
  },
  {
    ...baseStep,
    target: '[data-tutorial="projects-list"]',
    title: "Proyectos recurrentes",
    content:
      "Aquí aparecen los proyectos recurrentes ya generados para tus clientes, uno por cliente y mes. En el tutorial verás tres ejemplos, uno por fila.",
  },
  {
    ...baseStep,
    target: '[data-tutorial="tutorial-recurrent-project-row"]',
    title: "Cada proyecto sale de una plantilla central",
    content:
      "No se crean a mano: cada proyecto recurrente se genera con las fases y tareas del pack contratado por el cliente (columna «Plantilla»). Esa estructura la define la central mediante plantillas globales, por eso aquí no tienes botón de plantillas ni de nuevo proyecto.",
  },
  {
    ...baseStep,
    target: '[data-tutorial="tutorial-recurrent-project-row"]',
    title: "La recurrencia empieza tras el setup",
    content:
      "Cuando el proyecto de setup del cliente se completa, se activa su recurrencia. A partir de ahí, en cada próxima fecha programada se genera automáticamente el proyecto del mes siguiente.",
  },
];

export const RECURRENT_PROJECTS_MENU_TRIGGER_STEPS = [
  {
    ...baseStep,
    target: '[data-tutorial="tutorial-recurrent-menu-button"]',
    title: "Menú de acciones",
    content:
      "Cada fila tiene su menú de acciones en la columna «Acciones». Vamos a abrirlo para ver cómo consultar la recurrencia de este cliente.",
  },
];

export const RECURRENT_PROJECTS_MENU_STEPS = [
  {
    ...baseStep,
    target: '[data-tutorial="tutorial-recurrent-menu-item"]',
    title: "Configurar recurrencia",
    content:
      "Desde este menú cambias el estado del proyecto del mes y, con «Configurar recurrencia», abres la recurrencia del cliente sin salir del listado.",
    styles: {
      spotlight: {
        backgroundColor: "transparent",
      },
      spotlightLegacy: {
        backgroundColor: "transparent",
        boxShadow: "none",
      },
    },
  },
];

export const RECURRENT_PROJECTS_CONFIG_MODAL_STEPS = [
  {
    ...baseStep,
    target: '[data-tutorial="tutorial-recurring-config-modal"]',
    title: "Estado y próxima fecha",
    content:
      "Aquí ves si la recurrencia del cliente está activada y cuándo se generará el próximo proyecto. Puedes desactivarla para pausar la generación o cambiar la próxima fecha y guardar los cambios. En el ejemplo los campos son solo de lectura.",
    placement: "right",
  },
];

export const RECURRENT_PROJECTS_CLIENT_CARD_STEPS = [
  {
    ...baseStep,
    target: '[data-tutorial="tutorial-client-recurring-button"]',
    title: "También desde Clientes",
    content:
      "La misma configuración está en la página de Clientes: en la vista de tarjetas con este botón, y en la vista de tabla dentro del menú de acciones de cada cliente («Configurar recurrencia»).\n\nSolo está disponible cuando el proyecto de setup del cliente está completado; hasta entonces la opción aparece desactivada.",
    placement: "top",
  },
];

const RECURRENT_PROJECTS_PHASE_SEQUENCE = [
  [RECURRENT_PROJECTS_TUTORIAL_PHASES.OVERVIEW, RECURRENT_PROJECTS_OVERVIEW_STEPS],
  [RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU_TRIGGER, RECURRENT_PROJECTS_MENU_TRIGGER_STEPS],
  [RECURRENT_PROJECTS_TUTORIAL_PHASES.MENU, RECURRENT_PROJECTS_MENU_STEPS],
  [RECURRENT_PROJECTS_TUTORIAL_PHASES.CONFIG_MODAL, RECURRENT_PROJECTS_CONFIG_MODAL_STEPS],
  [RECURRENT_PROJECTS_TUTORIAL_PHASES.CLIENT_CARD, RECURRENT_PROJECTS_CLIENT_CARD_STEPS],
];

/** Pasos de una fase con la numeración global ya calculada. */
export const getRecurrentProjectsPhaseSteps = (phase) => {
  return numberPhaseSteps(RECURRENT_PROJECTS_PHASE_SEQUENCE, phase);
};
